import { useState, useCallback, useMemo } from 'react';
import {
  AdminBusiness,
  AdminBusinessDetail,
  AdminBusinessAgent,
  AdminBusinessClient,
  AdminBusinessAnalytics,
  BusinessFilters,
  AdminPagination,
} from '../../types/admin';

// ============================================================================
// MOCK DATA GENERATION
// ============================================================================

const BUSINESS_NAMES = [
  'Premium Realty Group',
  'Coastal Homes LLC',
  'Summit Properties',
  'Urban Living Realty',
  'Horizon Real Estate',
  'Legacy Home Group',
  'Vista Properties',
  'Parkside Realty',
  'Keystone Homes',
  'Evergreen Realty',
  'Apex Properties',
  'Beacon Real Estate',
  'Crestview Homes',
  'Diamond Realty Group',
  'Empire Properties',
  'Fairway Homes',
  'Gateway Realty',
  'Harbor Properties',
  'Imperial Real Estate',
  'Junction Homes',
  'Landmark Realty',
  'Metro Properties',
  'Nexus Real Estate',
  'Oasis Homes',
  'Pinnacle Realty',
  'Quantum Properties',
  'Redwood Realty Group',
  'Silverstone Homes',
  'Titan Real Estate',
  'Unity Properties',
  'Vanguard Realty',
  'Westside Homes',
  'Zenith Properties',
  'Aurora Real Estate',
  'Bridgepoint Realty',
  'Capitol Homes',
  'Daybreak Properties',
  'Eastgate Realty',
  'Fireside Homes',
  'Grandview Properties',
  'Hillside Realty',
  'Ironwood Homes',
  'Jasper Real Estate',
  'Kingsley Properties',
  'Lakeview Realty',
  'Mountain Peak Homes',
  'Northstar Properties',
  'Oakmont Realty',
  'Pacific Coast Homes',
];

const FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Christopher', 'Karen', 'Daniel', 'Nancy', 'Matthew', 'Lisa',
  'Anthony', 'Betty', 'Mark', 'Margaret', 'Donald', 'Sandra', 'Steven', 'Ashley',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
];

function randomDate(start: Date, end: Date): string {
  const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return date.toISOString();
}

function generateRandomName(): { firstName: string; lastName: string; fullName: string } {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
  };
}

function generateMockBusinesses(count: number): AdminBusiness[] {
  const businesses: AdminBusiness[] = [];
  const now = new Date();
  const threeYearsAgo = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());

  for (let i = 0; i < count; i++) {
    const businessName = BUSINESS_NAMES[i % BUSINESS_NAMES.length];
    const owner = generateRandomName();
    const createdAt = randomDate(threeYearsAgo, now);

    // Determine business size
    const sizeRand = Math.random();
    let agentsCount: number;
    let sizeCategory: 'small' | 'medium' | 'large';

    if (sizeRand < 0.4) {
      // 40% small (1-3 agents)
      agentsCount = Math.floor(Math.random() * 3) + 1;
      sizeCategory = 'small';
    } else if (sizeRand < 0.8) {
      // 40% medium (4-10 agents)
      agentsCount = Math.floor(Math.random() * 7) + 4;
      sizeCategory = 'medium';
    } else {
      // 20% large (11-25 agents)
      agentsCount = Math.floor(Math.random() * 15) + 11;
      sizeCategory = 'large';
    }

    // Clients per agent: 5-15 average
    const avgClientsPerAgent = Math.floor(Math.random() * 11) + 5;
    const clientsCount = agentsCount * avgClientsPerAgent;

    // Revenue calculation (more agents = more revenue)
    const avgRevenuePerClient = 2000 + Math.random() * 3000; // $2k-$5k per client
    const totalRevenue = Math.floor(clientsCount * avgRevenuePerClient);
    const monthlyRevenue = Math.floor(totalRevenue / 12);

    // Status: 90% active
    const status: 'active' | 'inactive' = Math.random() > 0.1 ? 'active' : 'inactive';

    // Last activity: within last 7 days for active, random for inactive
    const lastActivity =
      status === 'active'
        ? randomDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), now)
        : randomDate(new Date(createdAt), now);

    businesses.push({
      id: `business_${i + 1}`,
      name: businessName,
      owner_email: `${owner.firstName.toLowerCase()}.${owner.lastName.toLowerCase()}@${businessName.toLowerCase().replace(/\s+/g, '')}.com`,
      owner_name: owner.fullName,
      status,
      agents_count: agentsCount,
      clients_count: clientsCount,
      created_at: createdAt,
      last_activity: lastActivity,
      total_revenue: totalRevenue,
      monthly_revenue: monthlyRevenue,
      size_category: sizeCategory,
    });
  }

  return businesses;
}

function generateBusinessAgents(count: number, businessId: string): AdminBusinessAgent[] {
  const agents: AdminBusinessAgent[] = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const agent = generateRandomName();
    const createdAt = randomDate(new Date(now.getFullYear() - 2, 0, 1), now);
    const isActive = Math.random() > 0.05; // 95% active

    const clientsCount = Math.floor(Math.random() * 20) + 5; // 5-25 clients
    const activeClientsCount = Math.floor(clientsCount * (0.7 + Math.random() * 0.25)); // 70-95% active
    const conversionRate = 0.15 + Math.random() * 0.35; // 15-50% conversion rate

    const avgRevenuePerClient = 2000 + Math.random() * 3000;
    const totalRevenue = Math.floor(activeClientsCount * avgRevenuePerClient);
    const monthlyRevenue = Math.floor(totalRevenue / 12);

    const lastActivity = isActive
      ? randomDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), now)
      : randomDate(new Date(createdAt), now);

    agents.push({
      id: `agent_${businessId}_${i + 1}`,
      name: agent.fullName,
      email: `${agent.firstName.toLowerCase()}.${agent.lastName.toLowerCase()}@business.com`,
      picture: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
      is_active: isActive,
      clients_count: clientsCount,
      active_clients_count: activeClientsCount,
      conversion_rate: Math.round(conversionRate * 100) / 100,
      total_revenue: totalRevenue,
      monthly_revenue: monthlyRevenue,
      created_at: createdAt,
      last_activity: lastActivity,
    });
  }

  // Sort by revenue descending
  agents.sort((a, b) => b.total_revenue - a.total_revenue);

  return agents;
}

function generateBusinessClients(
  agents: AdminBusinessAgent[],
  businessId: string
): AdminBusinessClient[] {
  const clients: AdminBusinessClient[] = [];
  const now = new Date();
  let clientIndex = 0;

  agents.forEach((agent) => {
    const clientsForAgent = agent.clients_count;

    for (let i = 0; i < clientsForAgent; i++) {
      const client = generateRandomName();
      const createdAt = randomDate(new Date(agent.created_at), now);
      const isActive = clientIndex < agent.active_clients_count;
      const homesCount = Math.floor(Math.random() * 3) + 1; // 1-3 homes

      const lastActivity = isActive
        ? randomDate(new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000), now)
        : randomDate(new Date(createdAt), now);

      clients.push({
        id: `client_${businessId}_${clientIndex + 1}`,
        name: client.fullName,
        email: `${client.firstName.toLowerCase()}.${client.lastName.toLowerCase()}@email.com`,
        picture: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
        agent_name: agent.name,
        agent_email: agent.email,
        status: isActive ? 'active' : 'inactive',
        homes_count: homesCount,
        created_at: createdAt,
        last_activity: lastActivity,
      });

      clientIndex++;
    }
  });

  return clients;
}

function generateBusinessAnalytics(
  business: AdminBusiness,
  agents: AdminBusinessAgent[],
  clients: AdminBusinessClient[]
): AdminBusinessAnalytics {
  // Revenue history (last 6 months)
  const revenueHistory = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = month.toLocaleString('default', { month: 'short', year: 'numeric' });
    const variance = 0.8 + Math.random() * 0.4; // 80-120% of avg
    const revenue = Math.floor((business.monthly_revenue * variance) / 1000) * 1000; // Round to nearest 1000
    revenueHistory.push({
      month: monthName,
      revenue,
    });
  }

  // Calculate trend
  const recentRevenue = revenueHistory.slice(-2).reduce((sum, r) => sum + r.revenue, 0) / 2;
  const olderRevenue = revenueHistory.slice(0, 2).reduce((sum, r) => sum + r.revenue, 0) / 2;
  const revenueTrend = olderRevenue > 0 ? ((recentRevenue - olderRevenue) / olderRevenue) * 100 : 0;

  // Client metrics
  const activeClients = clients.filter((c) => c.status === 'active').length;
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const newThisMonth = clients.filter((c) => new Date(c.created_at) > oneMonthAgo).length;
  const churnRate = activeClients > 0 ? ((clients.length - activeClients) / clients.length) * 100 : 0;

  // Agent metrics
  const activeAgents = agents.filter((a) => a.is_active).length;
  const avgClientsPerAgent = activeAgents > 0 ? activeClients / activeAgents : 0;

  // Top performers (top 3 agents by revenue)
  const topPerformers = agents
    .slice(0, 3)
    .map((agent) => ({
      name: agent.name,
      revenue: agent.total_revenue,
      clients: agent.active_clients_count,
    }));

  // Performance metrics
  const totalConversions = agents.reduce(
    (sum, a) => sum + a.active_clients_count * a.conversion_rate,
    0
  );
  const conversionRate = clients.length > 0 ? totalConversions / clients.length : 0;
  const avgDealSize = activeClients > 0 ? business.total_revenue / activeClients : 0;
  const clientSatisfaction = 3.5 + Math.random() * 1.5; // 3.5-5.0 rating

  return {
    revenue: {
      total: business.total_revenue,
      monthly: business.monthly_revenue,
      trend: Math.round(revenueTrend * 10) / 10,
      history: revenueHistory,
    },
    clients: {
      total: clients.length,
      active: activeClients,
      new_this_month: newThisMonth,
      churn_rate: Math.round(churnRate * 10) / 10,
    },
    agents: {
      total: agents.length,
      active: activeAgents,
      avg_clients_per_agent: Math.round(avgClientsPerAgent * 10) / 10,
      top_performers: topPerformers,
    },
    performance: {
      conversion_rate: Math.round(conversionRate * 100) / 100,
      avg_deal_size: Math.round(avgDealSize),
      client_satisfaction: Math.round(clientSatisfaction * 10) / 10,
    },
  };
}

// Generate 50 mock businesses
const MOCK_BUSINESSES = generateMockBusinesses(50);

/**
 * useAdminBusinesses - Hook for business management operations
 *
 * Provides:
 * - Business list with pagination
 * - Search and filtering
 * - Business detail fetching
 * - Activate/deactivate businesses
 */
export function useAdminBusinesses() {
  const [allBusinesses] = useState<AdminBusiness[]>(MOCK_BUSINESSES);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<BusinessFilters>({});
  const [pagination, setPagination] = useState<Pick<AdminPagination, 'page' | 'limit'>>({
    page: 1,
    limit: 50,
  });
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);

  // Filter and search businesses
  const filteredBusinesses = useMemo(() => {
    let result = [...allBusinesses];

    // Search filter (name or owner email)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (business) =>
          business.name.toLowerCase().includes(searchLower) ||
          business.owner_email.toLowerCase().includes(searchLower) ||
          business.owner_name.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      result = result.filter((business) => business.status === filters.status);
    }

    // Size filter
    if (filters.size) {
      result = result.filter((business) => business.size_category === filters.size);
    }

    // Date range filter
    if (filters.created_from) {
      result = result.filter(
        (business) => new Date(business.created_at) >= new Date(filters.created_from!)
      );
    }
    if (filters.created_to) {
      result = result.filter(
        (business) => new Date(business.created_at) <= new Date(filters.created_to!)
      );
    }

    // Sort by last_activity descending (most recent first)
    result.sort(
      (a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime()
    );

    return result;
  }, [allBusinesses, filters]);

  // Paginated businesses
  const paginatedBusinesses = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    return filteredBusinesses.slice(startIndex, endIndex);
  }, [filteredBusinesses, pagination]);

  const totalBusinesses = filteredBusinesses.length;
  const totalPages = Math.ceil(totalBusinesses / pagination.limit);

  // Get business detail
  const getBusinessDetail = useCallback(
    (businessId: string): AdminBusinessDetail | null => {
      const business = allBusinesses.find((b) => b.id === businessId);
      if (!business) return null;

      // Generate agents
      const agents = generateBusinessAgents(business.agents_count, businessId);

      // Generate clients
      const clients = generateBusinessClients(agents, businessId);

      // Generate analytics
      const analytics = generateBusinessAnalytics(business, agents, clients);

      // Generate owner info
      const owner = {
        id: `user_${businessId}`,
        name: business.owner_name,
        email: business.owner_email,
        picture: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
        created_at: business.created_at,
      };

      return {
        ...business,
        owner,
        agents,
        clients,
        analytics,
      };
    },
    [allBusinesses]
  );

  // Search businesses
  const searchBusinesses = useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, search: query }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Filter businesses
  const filterBusinesses = useCallback((newFilters: Partial<BusinessFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination({ page: 1, limit: 50 });
  }, []);

  // Activate business
  const activateBusiness = useCallback(
    async (businessId: string) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Update business in mock data
        const businessIndex = allBusinesses.findIndex((b) => b.id === businessId);
        if (businessIndex !== -1) {
          allBusinesses[businessIndex] = {
            ...allBusinesses[businessIndex],
            status: 'active',
            last_activity: new Date().toISOString(),
          };
        }

        setIsLoading(false);
        return { success: true };
      } catch {
        setIsLoading(false);
        return { success: false, error: 'Failed to activate business' };
      }
    },
    [allBusinesses]
  );

  // Deactivate business
  const deactivateBusiness = useCallback(
    async (businessId: string) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Update business in mock data
        const businessIndex = allBusinesses.findIndex((b) => b.id === businessId);
        if (businessIndex !== -1) {
          allBusinesses[businessIndex] = {
            ...allBusinesses[businessIndex],
            status: 'inactive',
          };
        }

        setIsLoading(false);
        return { success: true };
      } catch {
        setIsLoading(false);
        return { success: false, error: 'Failed to deactivate business' };
      }
    },
    [allBusinesses]
  );

  // Refresh businesses (simulated)
  const refreshBusinesses = useCallback(async () => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsLoading(false);
  }, []);

  return {
    businesses: paginatedBusinesses,
    totalBusinesses,
    isLoading,
    error: null as string | null,
    pagination: {
      ...pagination,
      total: totalBusinesses,
      total_pages: totalPages,
    },
    filters,
    searchBusinesses,
    filterBusinesses,
    clearFilters,
    setPagination,
    activateBusiness,
    deactivateBusiness,
    getBusinessDetail,
    selectedBusinessId,
    setSelectedBusinessId,
    refreshBusinesses,
  };
}
